import * as THREE from "three";
import { useEffect, useRef, useState } from "react";
import {
  getDatetime,
  getStringNoLocale,
  getThingAll,
} from "@inrupt/solid-client";

import { TEXT_PREDICATE, CREATED_PREDICATE } from "../../lib/predicates";
import { addNewLineAtWordEnd } from "../../lib/util";

import { PointerLockControls } from "three/examples/jsm/controls/PointerLockControls.js";
import { Text } from "troika-three-text";

const ROAD_LENGTH = 2000;
const ROAD_WIDTH = 8;
const QUOTE_SPACING = 45;
const LATERAL_OFFSET = 5;
const MAX_QUOTES = 40;
const SIGN_MAX_WIDTH = 8;
const FONT_SIZE = 0.5;
const CHARS_PER_LINE = 22;
const LINE_HEIGHT = 0.65;
const SIGN_PADDING = 0.4;
const WALK_HEIGHT = 1.6;
const MOVE_SPEED = 60;

function RoadScene({ quoteList, onQuoteCount }) {
  const refContainer = useRef(null);
  const [showPlayPrompt, setShowPlayPrompt] = useState(true);

  useEffect(() => {
    const container = refContainer.current;
    if (!container) return;

    let camera, controls, scene, renderer;
    let rafId = 0;
    const moveState = { forward: 0, right: 0 };
    const velocity = new THREE.Vector3();

    let quoteArray = [];
    const safeQuoteList = Array.isArray(quoteList) ? quoteList : [];
    safeQuoteList.forEach((list) => {
      const quoteThings = list ? getThingAll(list) : [];
      const sorted = [...quoteThings].sort(
        (a, b) =>
          (getDatetime(b, CREATED_PREDICATE) || 0) -
          (getDatetime(a, CREATED_PREDICATE) || 0)
      );
      sorted.forEach((thing) => {
        const quote = getStringNoLocale(thing, TEXT_PREDICATE);
        if (quote) quoteArray.push(addNewLineAtWordEnd(quote));
      });
    });

    if (typeof onQuoteCount === "function") {
      onQuoteCount(quoteArray.length);
    }

    const count = Math.min(quoteArray.length, MAX_QUOTES);
    const textMeshes = [];

    function init() {
      camera = new THREE.PerspectiveCamera(
        70,
        window.innerWidth / window.innerHeight,
        0.1,
        3000
      );
      camera.position.set(0, WALK_HEIGHT, 10);

      scene = new THREE.Scene();
      scene.background = new THREE.Color(0x87ceeb);
      scene.fog = new THREE.Fog(0x87ceeb, 100, 800);

      scene.add(new THREE.AmbientLight(0xffffff, 0.85));
      const dir = new THREE.DirectionalLight(0xffffff, 0.4);
      dir.position.set(50, 100, 50);
      dir.castShadow = false;
      scene.add(dir);

      const groundGeo = new THREE.PlaneGeometry(300, ROAD_LENGTH + 200);
      groundGeo.rotateX(-Math.PI / 2);
      const ground = new THREE.Mesh(
        groundGeo,
        new THREE.MeshLambertMaterial({ color: 0x7cb342 })
      );
      ground.position.set(0, 0, -ROAD_LENGTH / 2);
      scene.add(ground);

      const roadGeo = new THREE.BoxGeometry(ROAD_WIDTH, 0.15, ROAD_LENGTH);
      const road = new THREE.Mesh(
        roadGeo,
        new THREE.MeshLambertMaterial({ color: 0x424242 })
      );
      road.position.set(0, 0.08, -ROAD_LENGTH / 2);
      scene.add(road);

      if (count > 0) {
        const signMat = new THREE.MeshLambertMaterial({ color: 0xf5f5dc });

        quoteArray.slice(0, count).forEach((quote, i) => {
          const z = -i * QUOTE_SPACING - 20;
          const x = (i % 2 === 0 ? -1 : 1) * LATERAL_OFFSET;
          const plainText = quote.replace(/\n/g, " ");
          const lineCount = Math.max(1, Math.ceil(plainText.length / CHARS_PER_LINE));
          const textHeight = lineCount * LINE_HEIGHT;
          const signHeight = textHeight + SIGN_PADDING;
          const signWidth = SIGN_MAX_WIDTH + SIGN_PADDING;

          const signGeo = new THREE.PlaneGeometry(signWidth, signHeight);
          signGeo.rotateY(x > 0 ? -Math.PI / 2 : Math.PI / 2);
          const sign = new THREE.Mesh(signGeo, signMat);
          sign.position.set(x, 2.1, z);
          scene.add(sign);

          const textMesh = new Text();
          scene.add(textMesh);
          textMesh.text = plainText;
          textMesh.fontSize = FONT_SIZE;
          textMesh.maxWidth = SIGN_MAX_WIDTH;
          textMesh.color = 0x1a1a1a;
          textMesh.anchorX = "center";
          textMesh.anchorY = "middle";
          textMesh.position.set(
            x + (x > 0 ? -0.04 : 0.04),
            2.1,
            z
          );
          textMesh.rotation.y = x > 0 ? -Math.PI / 2 : Math.PI / 2;
          textMesh.sync();
          textMeshes.push(textMesh);
        });
      }

      renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: "high-performance" });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.setSize(window.innerWidth, window.innerHeight);
      container.appendChild(renderer.domElement);

      controls = new PointerLockControls(camera, renderer.domElement);
      controls.pointerSpeed = 0.8;

      controls.domElement.addEventListener("click", () => {
        if (!controls.isLocked) controls.lock();
      });

      controls.addEventListener("lock", () => setShowPlayPrompt(false));
      controls.addEventListener("unlock", () => setShowPlayPrompt(true));

      const onKeyDown = (e) => {
        switch (e.code) {
          case "KeyW": moveState.forward = 1; break;
          case "KeyS": moveState.forward = -1; break;
          case "KeyA": moveState.right = -1; break;
          case "KeyD": moveState.right = 1; break;
          default: break;
        }
      };
      const onKeyUp = (e) => {
        switch (e.code) {
          case "KeyW":
          case "KeyS": moveState.forward = 0; break;
          case "KeyA":
          case "KeyD": moveState.right = 0; break;
          default: break;
        }
      };
      document.addEventListener("keydown", onKeyDown);
      document.addEventListener("keyup", onKeyUp);

      window.addEventListener("resize", onWindowResize);

      function onWindowResize() {
        camera.aspect = window.innerWidth / window.innerHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(window.innerWidth, window.innerHeight);
      }

      const clock = new THREE.Clock();

      function animate() {
        rafId = requestAnimationFrame(animate);
        const dt = Math.min(clock.getDelta(), 0.1);

        if (controls.isLocked) {
          velocity.set(0, 0, 0);
          if (moveState.forward) controls.moveForward(moveState.forward * MOVE_SPEED * dt);
          if (moveState.right) controls.moveRight(moveState.right * MOVE_SPEED * dt);
          camera.position.y = WALK_HEIGHT;
        }

        renderer.render(scene, camera);
      }

      animate();

      return () => {
        textMeshes.forEach((t) => {
          try {
            t.dispose?.();
          } catch (_) {}
        });
        cancelAnimationFrame(rafId);
        document.removeEventListener("keydown", onKeyDown);
        document.removeEventListener("keyup", onKeyUp);
        window.removeEventListener("resize", onWindowResize);
        controls.disconnect();
        if (document.pointerLockElement === renderer.domElement) {
          document.exitPointerLock();
        }
        if (container && renderer?.domElement) {
          container.removeChild(renderer.domElement);
        }
        renderer.dispose();
      };
    }

    return init();
  }, [quoteList]);

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <div ref={refContainer} style={{ width: "100%", height: "100%" }} />
      {showPlayPrompt && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.4)",
            color: "#fff",
            fontSize: "1.25rem",
            cursor: "pointer",
            userSelect: "none",
          }}
          onClick={() => refContainer.current?.querySelector("canvas")?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && refContainer.current?.querySelector("canvas")?.click()}
          aria-label="Click to enter and use WASD to move"
        >
          Click to enter — WASD to move, mouse to look
        </div>
      )}
    </div>
  );
}

export default RoadScene;
